import { FormEvent, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AlertCircle, Clock, Mail, MapPin, Phone, Send, CheckCircle2 } from 'lucide-react';
import axios from 'axios';
import Container from '../components/Container';
import SectionHeading from '../components/SectionHeading';
import { enquiriesApi } from '../../services/resources';
import { telHref, useSite } from '../../utils/site';

interface FormState {
  name: string;
  email: string;
  phone: string;
  subject: string;
  message: string;
  website: string;
}

const INITIAL_STATE: FormState = {
  name: '',
  email: '',
  phone: '',
  subject: '',
  message: '',
  website: '',
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function Contact() {
  const site = useSite();
  const [searchParams] = useSearchParams();
  const [form, setForm] = useState<FormState>({
    ...INITIAL_STATE,
    subject: searchParams.get('subject') ?? '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (field: keyof FormState) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
  };

  function validate(): Record<string, string> {
    const next: Record<string, string> = {};
    if (!form.name.trim()) next.name = 'Please enter your name.';
    if (!form.email.trim()) next.email = 'Please enter your email address.';
    else if (!EMAIL_RE.test(form.email.trim())) next.email = 'Please enter a valid email address.';
    if (!form.message.trim()) next.message = 'Please enter a message.';
    return next;
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setSubmitError(null);

    const validationErrors = validate();
    setErrors(validationErrors);
    if (Object.keys(validationErrors).length > 0) return;

    setSubmitting(true);
    try {
      await enquiriesApi.create({ ...form } as any);
      setSubmitted(true);
    } catch (err) {
      if (axios.isAxiosError(err) && err.response?.status === 422) {
        const detail = err.response.data?.detail;
        const fieldErrors: Record<string, string> = {};
        if (Array.isArray(detail)) {
          detail.forEach((d: { loc?: string[]; msg?: string }) => {
            const field = d.loc?.[d.loc.length - 1];
            if (field) fieldErrors[field] = d.msg ?? 'This value is not valid.';
          });
        }
        setErrors(fieldErrors);
        setSubmitError('Please fix the highlighted fields and try again.');
      } else {
        setSubmitError('Could not reach the server. Please check your connection and try again.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <section id="contact" className="scroll-mt-20">
      <section className="bg-gradient-to-b from-brand-green-50 to-surface py-16 sm:py-6">
        <Container>
          <SectionHeading
            eyebrow="Contact Us"
            title="We'd love to hear from you"
            subtitle="Whether it's a bulk order, a question about our products, or a visit to the farm - send us a message and our team will get back to you."
            center
          />
        </Container>
      </section>

      <section className="py-16 sm:py-20">
        <Container className="grid gap-12 lg:grid-cols-5">
          {/* Contact details */}
          <div className="space-y-6 lg:col-span-2">
            <div className="rounded-xl border border-brand-green-100 bg-surface p-6 shadow-card">
              <div className="flex items-start gap-3">
                <MapPin className="mt-0.5 h-5 w-5 shrink-0 text-brand-gold-600" />
                <div>
                  <h3 className="text-sm font-semibold text-brand-green-900">Address</h3>
                  <p className="mt-1 text-sm text-ink-500">
                    <a
                      href="https://maps.google.com/maps?q=12.939070701599121%2C77.79291534423828&z=17&hl=en"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="hover:text-brand-green-700"
                    >
                      {site.address}
                    </a>
                  </p>
                </div>
              </div>
            </div>
            <div className="rounded-xl border border-brand-green-100 bg-surface p-6 shadow-card">
              <div className="flex items-start gap-3">
                <Phone className="mt-0.5 h-5 w-5 shrink-0 text-brand-gold-600" />
                <div>
                  <h3 className="text-sm font-semibold text-brand-green-900">Phone</h3>
                  <p className="mt-1 text-sm text-ink-500">
                    <a href={telHref(site.phone)} className="hover:text-brand-green-700">{site.phone}</a>
                  </p>
                </div>
              </div>
            </div>
            <div className="rounded-xl border border-brand-green-100 bg-surface p-6 shadow-card">
              <div className="flex items-start gap-3">
                <Mail className="mt-0.5 h-5 w-5 shrink-0 text-brand-gold-600" />
                <div>
                  <h3 className="text-sm font-semibold text-brand-green-900">Email</h3>
                  <p className="mt-1 text-sm text-ink-500">
                    <a href={`mailto:${site.email}`} className="hover:text-brand-green-700">{site.email}</a>
                  </p>
                </div>
              </div>
            </div>
            <div className="rounded-xl border border-brand-green-100 bg-surface p-6 shadow-card">
              <div className="flex items-start gap-3">
                <Clock className="mt-0.5 h-5 w-5 shrink-0 text-brand-gold-600" />
                <div>
                  <h3 className="text-sm font-semibold text-brand-green-900">Business Hours</h3>
                  <p className="mt-1 text-sm text-ink-500">{site.business_hours || 'Please call ahead to plan a visit.'}</p>
                </div>
              </div>
            </div>

            <div className="aspect-[4/3] w-full overflow-hidden rounded-xl border border-brand-green-100 shadow-card">
              <iframe
                title="Sri KalpaGavya farm location"
                src="https://maps.google.com/maps?q=12.939070701599121%2C77.79291534423828&z=17&hl=en&output=embed"
                className="h-full w-full border-0"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </div>

          {/* Form */}
          <div className="lg:col-span-3">
            <div className="rounded-xl border border-brand-green-100 bg-surface p-6 shadow-card sm:p-8">
              {submitted ? (
                <div className="flex flex-col items-center gap-4 py-10 text-center">
                  <CheckCircle2 className="h-14 w-14 text-brand-green-600" />
                  <h3 className="text-xl font-bold text-brand-green-900">Thank you for reaching out!</h3>
                  <p className="max-w-sm text-sm text-ink-500">
                    We've received your message and will get back to you as soon as possible.
                  </p>
                  <button
                    type="button"
                    onClick={() => {
                      setForm(INITIAL_STATE);
                      setErrors({});
                      setSubmitted(false);
                    }}
                    className="mt-2 text-sm font-semibold text-brand-green-700 hover:text-brand-green-800"
                  >
                    Send another message
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} noValidate>
                  {submitError && (
                    <div className="mb-6 flex items-start gap-3 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700">
                      <AlertCircle className="mt-0.5 h-5 w-5 shrink-0" />
                      <span>{submitError}</span>
                    </div>
                  )}

                  <div className="grid gap-5 sm:grid-cols-2">
                    <div>
                      <label htmlFor="name" className="mb-1.5 block text-sm font-semibold text-brand-green-900">
                        Name <span className="text-red-600">*</span>
                      </label>
                      <input
                        id="name"
                        type="text"
                        value={form.name}
                        onChange={handleChange('name')}
                        className={`w-full rounded-lg border px-3.5 py-2.5 text-sm outline-none transition-colors focus:border-brand-green-500 focus:ring-1 focus:ring-brand-green-500 ${
                          errors.name ? 'border-red-500' : 'border-ink-300'
                        }`}
                        autoComplete="name"
                      />
                      {errors.name && <p className="mt-1.5 text-xs text-red-600">{errors.name}</p>}
                    </div>

                    <div>
                      <label htmlFor="email" className="mb-1.5 block text-sm font-semibold text-brand-green-900">
                        Email <span className="text-red-600">*</span>
                      </label>
                      <input
                        id="email"
                        type="email"
                        value={form.email}
                        onChange={handleChange('email')}
                        className={`w-full rounded-lg border px-3.5 py-2.5 text-sm outline-none transition-colors focus:border-brand-green-500 focus:ring-1 focus:ring-brand-green-500 ${
                          errors.email ? 'border-red-500' : 'border-ink-300'
                        }`}
                        autoComplete="email"
                      />
                      {errors.email && <p className="mt-1.5 text-xs text-red-600">{errors.email}</p>}
                    </div>

                    <div>
                      <label htmlFor="phone" className="mb-1.5 block text-sm font-semibold text-brand-green-900">
                        Phone
                      </label>
                      <input
                        id="phone"
                        type="tel"
                        value={form.phone}
                        onChange={handleChange('phone')}
                        className={`w-full rounded-lg border px-3.5 py-2.5 text-sm outline-none transition-colors focus:border-brand-green-500 focus:ring-1 focus:ring-brand-green-500 ${
                          errors.phone ? 'border-red-500' : 'border-ink-300'
                        }`}
                        autoComplete="tel"
                      />
                      {errors.phone && <p className="mt-1.5 text-xs text-red-600">{errors.phone}</p>}
                    </div>

                    <div>
                      <label htmlFor="subject" className="mb-1.5 block text-sm font-semibold text-brand-green-900">
                        Subject
                      </label>
                      <input
                        id="subject"
                        type="text"
                        value={form.subject}
                        onChange={handleChange('subject')}
                        className={`w-full rounded-lg border px-3.5 py-2.5 text-sm outline-none transition-colors focus:border-brand-green-500 focus:ring-1 focus:ring-brand-green-500 ${
                          errors.subject ? 'border-red-500' : 'border-ink-300'
                        }`}
                      />
                      {errors.subject && <p className="mt-1.5 text-xs text-red-600">{errors.subject}</p>}
                    </div>

                    <div className="sm:col-span-2">
                      <label htmlFor="message" className="mb-1.5 block text-sm font-semibold text-brand-green-900">
                        Message <span className="text-red-600">*</span>
                      </label>
                      <textarea
                        id="message"
                        rows={5}
                        value={form.message}
                        onChange={handleChange('message')}
                        className={`w-full rounded-lg border px-3.5 py-2.5 text-sm outline-none transition-colors focus:border-brand-green-500 focus:ring-1 focus:ring-brand-green-500 ${
                          errors.message ? 'border-red-500' : 'border-ink-300'
                        }`}
                      />
                      {errors.message && <p className="mt-1.5 text-xs text-red-600">{errors.message}</p>}
                    </div>

                    {/* Honeypot: real users never see or fill this. Kept as a real,
                        visible-to-bots text input (not type="hidden") but moved
                        off-screen with CSS so a naive bot that fills every field
                        still trips it. */}
                    <div className="hp-field" aria-hidden="true">
                      <label htmlFor="website">Website</label>
                      <input
                        id="website"
                        name="website"
                        type="text"
                        tabIndex={-1}
                        autoComplete="off"
                        value={form.website}
                        onChange={handleChange('website')}
                      />
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={submitting}
                    className="mt-7 inline-flex items-center gap-2 rounded-full bg-brand-green-700 px-7 py-3 text-sm font-semibold text-white shadow-card transition-colors hover:bg-brand-green-800 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    <Send className="h-4 w-4" />
                    {submitting ? 'Sending...' : 'Send Message'}
                  </button>
                </form>
              )}
            </div>
          </div>
        </Container>
      </section>
    </section>
  );
}
